import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, UploadCloud } from "lucide-react";
import Logo from "@/components/Logo";

const BG_OPTIONS = [
  "Bengali is my native language",
  "I grew up bilingual with Bengali",
  "I am fluent in Bengali through education or professional experience",
  "I learned Bengali later and have advanced proficiency",
  "Other",
];

const DOMAIN_OPTIONS = ["Lawyer", "Doctor", "Engineer", "Coder", "Indian Language Expert", "Other (Specify)"];

export default function Waitlist() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", domain: "", domain_other: "", linkedin: "", description: "" });
  const [resume, setResume] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isOther = form.domain === "Other (Specify)";

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.domain) { toast.error("Please provide your name, email and primary domain."); return; }
    if (form.phone.trim().length < 6) { toast.error("Please enter a valid phone number."); return; }
    if (isOther && !form.domain_other.trim()) { toast.error("Please specify your domain."); return; }
    if (resume) {
      const ext = resume.name.toLowerCase();
      if (!(ext.endsWith(".pdf") || ext.endsWith(".doc") || ext.endsWith(".docx"))) { toast.error("Resume must be PDF, DOC or DOCX."); return; }
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (resume) fd.append("resume", resume);
    setSaving(true);
    try { await api.post("/waitlist", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDone(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not join the waitlist."); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F6F3EC]">
      <header className="w-full border-b border-[#E5E5E5] bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button data-testid="back-home-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-medium text-[#525252] hover:text-[#0A0A0A]"><ArrowLeft className="h-4 w-4" /> Back to home</button>
          <Logo variant="dark" to={null} />
        </div>
      </header>
      <div className="max-w-xl mx-auto px-6 py-12">
        {done ? (
          <div data-testid="waitlist-done" className="bg-white border border-[#059669] rounded-sm p-8">
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#059669]">You're on the waitlist</h1>
            <p className="mt-2 text-sm text-[#525252]">FIAD is currently opening selected cohorts and pathways. We may contact you when a relevant cohort or opportunity becomes available.</p>
            <button onClick={() => navigate("/")} className="mt-6 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-sm text-sm font-medium">Back to home</button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tight">Join the waitlist</h1>
            <p className="mt-2 text-sm text-[#525252]">We're currently onboarding Bengali linguistic experts. Tell us about your domain and we may contact you when a relevant pathway opens.</p>
            <div className="mt-8 bg-white border border-[#E5E5E5] rounded-sm p-6 space-y-5">
              <div><label className="block text-xs font-medium text-[#525252] mb-1.5">Name</label><input data-testid="wl-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" /></div>
              <div><label className="block text-xs font-medium text-[#525252] mb-1.5">Email</label><input data-testid="wl-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" /></div>
              <div><label className="block text-xs font-medium text-[#525252] mb-1.5">Phone number</label><input data-testid="wl-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91…" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" /></div>
              <div>
                <label className="block text-xs font-medium text-[#525252] mb-1.5">Primary domain / expertise</label>
                <select data-testid="wl-domain" value={form.domain} onChange={(e) => set("domain", e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-black">
                  <option value="">Select a domain</option>
                  {DOMAIN_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {isOther && <div><label className="block text-xs font-medium text-[#525252] mb-1.5">Please specify your domain</label><input data-testid="wl-domain-other" value={form.domain_other} onChange={(e) => set("domain_other", e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" /></div>}
              <div><label className="block text-xs font-medium text-[#525252] mb-1.5">LinkedIn Profile URL (Optional)</label><input data-testid="wl-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/in/…" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" /></div>
              <div>
                <label className="block text-xs font-medium text-[#525252] mb-1.5">Resume (Optional · PDF, DOC, DOCX)</label>
                <label className="flex items-center gap-3 border-2 border-dashed border-[#E5E5E5] rounded-sm p-3 cursor-pointer hover:bg-[#F5F5F5]">
                  <UploadCloud className="h-4 w-4 text-[#525252]" />
                  <span className="text-sm text-[#525252]">{resume ? resume.name : "Upload your resume"}</span>
                  <input data-testid="wl-resume" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div><label className="block text-xs font-medium text-[#525252] mb-1.5">Short description (optional)</label><textarea data-testid="wl-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" /></div>
              <button data-testid="wl-submit" onClick={submit} disabled={saving} className="w-full bg-[#0A0A0A] text-white px-5 py-3 rounded-sm text-sm font-medium hover:bg-[#262626] disabled:opacity-60">{saving ? "Joining…" : "Join waitlist"}</button>
            </div>
            <p className="mt-4 text-xs text-[#A3A3A3]">By joining you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
          </>
        )}
      </div>
    </div>
  );
}

export { BG_OPTIONS };
